import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Download, 
  Activity,
  Clock,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  Eye,
  Package,
  FileText,
  User,
  Building2
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ColumnConfigDropdown, ColumnConfig } from '@/components/ColumnConfigDropdown';
import { Pagination } from '@/components/ui/pagination';
import { apiRequest } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

interface ActivityLog {
  id: string;
  type: string;
  description: string;
  packageId?: string;
  packageNumber?: string;
  submissionId?: string;
  userName: string;
  metadata: any;
  daysAgo?: string;
  createdAt: string;
  projectId?: string;
  hubName?: string;
  projectName?: string;
}

const AdminAuditLogs: React.FC = () => {
  const { toast } = useToast();
  
  // 从真实数据库获取activities数据
  const { data: activities = [], isLoading, error, refetch } = useQuery<ActivityLog[]>({
    queryKey: ['/api/activities'],
    queryFn: async () => {
      const data = await apiRequest('/api/activities');
      return data || [];
    },
    // 每30秒自动刷新一次，确保数据同步
    refetchInterval: 30000,
    // 当窗口重新获得焦点时刷新
    refetchOnWindowFocus: true,
    // 当网络重新连接时刷新
    refetchOnReconnect: true,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [hubFilter, setHubFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isScrolled, setIsScrolled] = useState(false);
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'type', label: 'Activity Type', visible: true, sortable: true, width: 'w-40', frozen: true },
    { key: 'description', label: 'Description', visible: true, sortable: false, width: 'w-96' },
    { key: 'userName', label: 'User', visible: true, sortable: true, width: 'w-48' },
    { key: 'packageNumber', label: 'Package', visible: true, sortable: false, width: 'w-32' },
    { key: 'projectId', label: 'Project', visible: true, sortable: false, width: 'w-48' },
    { key: 'hubName', label: 'Hub', visible: true, sortable: false, width: 'w-40' },
    { key: 'createdAt', label: 'Timestamp', visible: true, sortable: true, width: 'w-44' },
  ]);

  // 滚动检测
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ✅ 新增：两个滚动容器的 ref
  const topScrollRef = React.useRef<HTMLDivElement>(null);
  const bodyScrollRef = React.useRef<HTMLDivElement>(null);

  // ✅ 新增：同步函数（顶部 ⇄ 主体）
  const syncFromTop = () => {
    if (!topScrollRef.current || !bodyScrollRef.current) return;
    // 把顶部滚动条的 scrollLeft 同步给表格容器
    bodyScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
  };

  const syncFromBody = () => {
    if (!topScrollRef.current || !bodyScrollRef.current) return;
    // 把表格容器的 scrollLeft 同步给顶部滚动条
    topScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    // 保留水平滚动阴影逻辑
    setIsScrolled(bodyScrollRef.current.scrollLeft > 0);
  };

  // ✅ 添加CSS来隐藏顶部滚动条的滚动条（Webkit浏览器）
  React.useEffect(() => {
    const style = document.createElement('style');
    style.id = 'hide-top-scrollbar-audit';
    style.textContent = '.hide-top-scrollbar-audit::-webkit-scrollbar { display: none; }';
    document.head.appendChild(style);
    
    return () => {
      const existingStyle = document.getElementById('hide-top-scrollbar-audit');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  // 过滤和搜索逻辑
  const filteredActivities = activities.filter(activity => {
    const matchesSearch = activity.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         activity.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (activity.packageNumber && activity.packageNumber.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = typeFilter === 'all' || activity.type === typeFilter;
    const matchesProject = projectFilter === 'all' || (activity.projectId && activity.projectId.includes(projectFilter));
    const matchesHub = hubFilter === 'all' || (activity.hubName && activity.hubName === hubFilter);
    
    return matchesSearch && matchesType && matchesProject && matchesHub;
  });

  // 排序逻辑
  const sortedActivities = [...filteredActivities].sort((a, b) => {
    if (!sortField) return 0;
    
    let aValue = '';
    let bValue = '';
    
    switch (sortField) {
      case 'createdAt':
        aValue = a.createdAt;
        bValue = b.createdAt;
        break;
      case 'type':
        aValue = a.type;
        bValue = b.type;
        break;
      case 'userName':
        aValue = a.userName;
        bValue = b.userName;
        break;
      default:
        return 0;
    }
    
    if (sortDirection === 'asc') {
      return (aValue || '').localeCompare(bValue || '');
    } else {
      return (bValue || '').localeCompare(aValue || '');
    }
  });

  // 分页逻辑
  const totalItems = filteredActivities.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedActivities = sortedActivities.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const visibleColumns = columns.filter(col => col.visible);

  // 获取活动类型的徽章
  const getActivityTypeBadge = (type: string) => {
    const typeConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      package_created: { label: 'Package Created', variant: 'default' },
      package_submitted: { label: 'Package Submitted', variant: 'secondary' },
      package_viewed: { label: 'Package Viewed', variant: 'outline' },
      package_downloaded: { label: 'Package Downloaded', variant: 'outline' },
      tender_returned: { label: 'Package Returned', variant: 'secondary' },
      tender_awarded: { label: 'Package Awarded', variant: 'default' },
      tender_rejected: { label: 'Package Rejected', variant: 'destructive' },
      package_awarded: { label: 'Package Awarded', variant: 'default' },
      files_added: { label: 'Files Added', variant: 'outline' },
    };

    const config = typeConfig[type] || { label: type, variant: 'outline' as const };
    return (
      <Badge variant={config.variant} className="text-xs">
        {config.label}
      </Badge>
    );
  };

  // 格式化日期时间
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // 渲染单元格内容
  const renderCell = (activity: ActivityLog, columnKey: string) => {
    switch (columnKey) {
      case 'type':
        return getActivityTypeBadge(activity.type);
      case 'description':
        return (
          <div className="max-w-md">
            <span className="text-sm text-gray-900 line-clamp-2" title={activity.description}>
              {activity.description}
            </span>
          </div>
        );
      case 'userName':
        return (
          <div className="flex items-center space-x-2">
            <User className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-900 truncate" title={activity.userName}>
              {activity.userName}
            </span>
          </div>
        );
      case 'packageNumber':
        return activity.packageNumber ? (
          <div className="flex items-center space-x-1">
            <Package className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-medium text-blue-600">
              {activity.packageNumber}
            </span>
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        );
      case 'projectId':
        return activity.projectId ? (
          <div className="flex items-center space-x-1">
            <Building2 className="w-4 h-4 text-purple-500" />
            <span className="text-sm text-gray-600 truncate font-mono" title={activity.projectId}>
              {activity.projectId.split('-')[0]}...
            </span>
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        );
      case 'hubName':
        return activity.hubName || activity.metadata?.hubName ? (
          <span className="text-sm font-medium text-green-600">
            {activity.hubName || activity.metadata?.hubName}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        );
      case 'createdAt':
        return (
          <div className="flex items-center space-x-1">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600">
              {formatDateTime(activity.createdAt)}
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  // 导出CSV
  const exportToCSV = () => {
    const headers = ['Activity Type', 'Description', 'User', 'Package', 'Project', 'Hub', 'Timestamp'];
    const csvContent = [
      headers.join(','),
      ...filteredActivities.map(activity => [
        activity.type,
        `"${activity.description.replace(/"/g, '""')}"`,
        activity.userName,
        activity.packageNumber || '',
        activity.projectId || '',
        activity.hubName || activity.metadata?.hubName || '',
        formatDateTime(activity.createdAt)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'audit-logs-export.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // 获取唯一的活动类型、项目和Hub列表用于过滤
  const uniqueTypes = Array.from(new Set(activities.map(activity => activity.type)));
  const uniqueProjects = Array.from(new Set(activities.map(activity => activity.projectId).filter(Boolean)));
  const uniqueHubs = Array.from(new Set(activities.map(activity => 
    activity.hubName || activity.metadata?.hubName
  ).filter(Boolean)));

  // 处理加载状态
  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading activities...</p>
          </div>
        </div>
      </div>
    );
  }

  // 处理错误状态
  if (error) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-600 text-lg font-medium">Error loading activities</div>
            <p className="mt-2 text-gray-600">Please try again later</p>
            <Button 
              onClick={() => refetch()}
              className="mt-4"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Activity className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Activities</p>
                <p className="text-2xl font-bold text-gray-900">{activities.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Package className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Package Activities</p>
                <p className="text-2xl font-bold text-gray-900">
                  {activities.filter(a => a.packageId).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <FileText className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Today's Activities</p>
                <p className="text-2xl font-bold text-gray-900">
                  {activities.filter(a => {
                    const today = new Date().toDateString();
                    const activityDate = new Date(a.createdAt).toDateString();
                    return today === activityDate;
                  }).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Building2 className="w-8 h-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Projects</p>
                <p className="text-2xl font-bold text-gray-900">
                  {uniqueProjects.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search activities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {uniqueTypes.map(type => (
                    <SelectItem key={type} value={type}>{type.replace('_', ' ').toUpperCase()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {uniqueProjects.map(project => (
                    <SelectItem key={project} value={project}>{project}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={hubFilter} onValueChange={setHubFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by hub" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Hubs</SelectItem>
                  {uniqueHubs.map(hub => (
                    <SelectItem key={hub} value={hub}>{hub}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="overflow-hidden" style={{marginBottom: '8px'}}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-bold text-gray-900">Project Activities Audit Log</CardTitle>
              <p className="text-sm text-gray-600 mt-1">Track all project and package activities across all hubs</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2"
              >
                <Search className="w-4 h-4" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
              
              <ColumnConfigDropdown 
                columns={columns}
                onColumnsChange={setColumns}
              />
              
              <Button variant="outline" size="sm" onClick={exportToCSV}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              
              <Button size="sm" onClick={() => refetch()}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">

          {/* ✅ 新增：顶部"镜像滚动条"——完全隐藏但保持滚动功能 */}
          <div
            ref={topScrollRef}
            onScroll={syncFromTop}
            className="w-full overflow-x-auto hide-top-scrollbar-audit"
            style={{ 
              height: 0,
              scrollbarGutter: 'stable',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            <div style={{ width: '1400px', height: 1 }} />
          </div>

          {/* ✅ 修改：主滚动容器 */}
          <div
            ref={bodyScrollRef}
            className="overflow-x-auto overflow-y-scroll"
            style={{
              height: 'calc(100vh - 160px)',
              maxWidth: '100%',
            }}
            onScroll={syncFromBody}
          >
            {/* 表格本体 */}
            <Table className="border-collapse" style={{ minWidth: '1400px', width: '1400px' }}>
              {/* ❗️注意：这里不要再给 TableHeader 外层加 sticky，
                   让每个 <TableHead/>（th）自己 sticky（我们在组件里已经改好了）
              */}
              <TableHeader>
                <TableRow>
                  {visibleColumns.map((column) => (
                    <TableHead 
                      key={column.key} 
                      className={`${column.width} py-2${column.sortable ? ' cursor-pointer hover:bg-gray-50' : ''}${column.frozen ? ' sticky left-0 z-40 bg-gray-50 shadow-lg' : ''}${column.frozen && isScrolled ? ' border-r border-gray-300' : ''}`}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      <div className="flex items-center gap-2">
                        <span>{column.label}</span>
                        {column.sortable && sortField === column.key && (
                          sortDirection === 'asc' ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )
                        )}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedActivities.map((activity) => (
                  <TableRow key={activity.id} className="hover:bg-gray-50">
                    {visibleColumns.map((column) => (
                      <TableCell key={column.key} className={`${column.width} py-2${column.frozen ? ' sticky left-0 z-20 bg-white shadow-lg hover:bg-white' : ''}${column.frozen && isScrolled ? ' border-r border-gray-300' : ''}`}>
                        {renderCell(activity, column.key)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

                {/* 完全移除空白行，让表格更紧凑 */}
              </TableBody>
            </Table>
          </div>

          {/* ✅ 分页区域保持在滚动条下面（也就是"滚动条在分页上方"） */}
          <div className="bg-white">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAuditLogs;